import { ImageOff } from 'lucide-react'
import type { ReactNode } from 'react'

import { UNITS } from '@aeris/catalog'
import type { ProductColumnId, ProductSummary } from '@aeris/contracts'
import {
  ZERO_QUANTITY,
  formatQuantity as formatUnits,
  parseQuantity,
} from '@aeris/inventory'
import {
  MONEY_SCALE,
  UNIT_PRICE_SCALE,
  ZERO_MONEY,
  formatMoney,
  formatPercent,
  fromString,
  parseHumanAmount,
} from '@aeris/money'
import { marginFromPrice } from '@aeris/pricing'
import { Badge, Tooltip, VisuallyHidden } from '@aeris/ui'

import { formatDate } from './date-format.ts'
import type { ColumnRule } from './table-columns.ts'

/**
 * As colunas da lista de Produtos — F127.
 *
 * Tudo o que a lista sabe mostrar mora aqui, uma entrada por coluna: o rótulo
 * do menu, quem pode ligar, como ordena e como a célula se desenha. A tela
 * gera cabeçalho e linha a partir desta lista, então uma coluna nova é uma
 * entrada — e o id dela tem de existir em `PRODUCT_COLUMN_IDS`, que é o que o
 * servidor aceita gravar.
 *
 * Quem pode ver cada coluna é a mesma regra que já valia: preço com
 * `pricing.read`, estoque com `inventory.read`, custo e margem só para quem
 * edita o produto. A rota da lista também não entrega o que o papel não
 * alcança; esconder aqui é só não oferecer uma coluna que viria vazia.
 */

/**
 * As colunas por que a lista sabe se ordenar — F185.
 *
 * O dono: "pra ver custo médio não dá… valor em custo, valor de venda,
 * disponível… tudo". Cada uma destas tem um ramo no `ORDER BY` do servidor;
 * as que ficaram de fora ficaram por custo, e o motivo está no documento da
 * F185 (última compra e preço alterado em pedem agregado sobre o razão de
 * movimentos e sobre o histórico de preço).
 */
export type ProductSortKey =
  | 'name'
  | 'createdAt'
  | 'updatedAt'
  | 'brand'
  | 'category'
  | 'condition'
  | 'status'
  | 'sku'
  | 'basePrice'
  | 'averageCost'
  | 'margin'
  | 'onHand'
  | 'available'

export type ProductColumn = ColumnRule<ProductColumnId> & {
  /** O que vai no cabeçalho quando não é o rótulo (a foto não tem título). */
  readonly header?: ReactNode
  readonly headerClassName?: string
  readonly sortKey?: ProductSortKey
  readonly numeric?: boolean
  readonly className?: string
  readonly cell: (product: ProductSummary) => ReactNode
}

/** As colunas de antes da F127: quem nunca escolheu vê a lista de sempre. */

/**
 * Conta estoque, a lista trouxe o saldo desta filial, e ele é zero ou menos
 * (F214). Pelo texto, sem `Number`: quantidade não passa por float.
 */
export const outOfStockHere = (product: ProductSummary): boolean => {
  if (!product.tracksStock || product.stock == null) return false
  const onHand = product.stock.onHand.trim()
  return onHand.startsWith('-') || /^0*(\.0*)?$/.test(onHand)
}

export const DEFAULT_PRODUCT_COLUMNS: readonly ProductColumnId[] = [
  'photo',
  'sku',
  'name',
  'brand',
  'unit',
  'gtin',
  'price',
  'stock',
]

const defaultVariant = (product: ProductSummary) =>
  product.variants.find((variant) => variant.isDefault)

/**
 * Do texto decimal exato, nunca de um `Number()`.
 *
 * ADR-0016: nenhum valor de dinheiro ou quantidade é número de JavaScript, em
 * camada nenhuma, nem na que só formata.
 */
const asMoney = (value: string): string =>
  formatMoney(parseHumanAmount(value, MONEY_SCALE) ?? ZERO_MONEY)

const asQuantity = (value: string): string =>
  formatUnits(parseQuantity(value) ?? ZERO_QUANTITY)

const dash = <span className="text-(--text-muted)">—</span>

export const CONDITION_LABEL: Record<ProductSummary['condition'], string> = {
  new: 'Novo',
  used: 'Usado',
  refurbished: 'Recondicionado',
}

export const PRODUCT_COLUMNS: readonly ProductColumn[] = [
  {
    id: 'photo',
    label: 'Foto',
    requires: [],
    // Sem título visível: um cabeçalho sobre uma faixa de miniaturas é ruído,
    // e as imagens carregam o próprio texto alternativo.
    header: <VisuallyHidden>Foto</VisuallyHidden>,
    headerClassName: 'w-12',
    cell: (product) =>
      product.primaryImage === null ? (
        // Caixa fixa nos dois casos: linhas com e sem foto se alinham, e a
        // tabela não pula quando as imagens chegam.
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-sunken) text-(--text-muted)"
        >
          <ImageOff className="h-4 w-4" />
        </span>
      ) : (
        <img
          src={product.primaryImage.thumbUrl}
          alt={product.primaryImage.altText ?? ''}
          width={36}
          height={36}
          loading="lazy"
          className="h-9 w-9 rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-sunken) object-contain"
        />
      ),
  },
  {
    id: 'sku',
    label: 'Código',
    sortKey: 'sku',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap',
    cell: (product) => defaultVariant(product)?.sku ?? '—',
  },
  {
    id: 'name',
    label: 'Nome',
    requires: [],
    locked: true,
    sortKey: 'name',
    cell: (product) => (
      <>
        <span className="text-(--text-primary)">{product.name}</span>
        {/* F176: o produto constituído por cores. */}
        {product.variants.filter((entry) => entry.status === 'active').length >
        1 ? (
          <Badge tone="info" className="ml-2">
            {`${String(
              product.variants.filter((entry) => entry.status === 'active')
                .length
            )} cores`}
          </Badge>
        ) : null}
        {product.status === 'archived' ? (
          <Badge tone="neutral" className="ml-2">
            Arquivado
          </Badge>
        ) : null}
        {/*
          F214: sem estoque, grande e vermelho — a loja precisa ver de longe,
          mesmo com a coluna de estoque escondida.
        */}
        {outOfStockHere(product) ? (
          <Badge tone="danger" className="ml-2">
            Sem estoque
          </Badge>
        ) : null}
        {/* F133: existe e não vende até alguém conferir o estoque. */}
        {product.awaitingStockSince !== null && product.status === 'active' ? (
          <Badge tone="warning" className="ml-2">
            Aguardando estoque
          </Badge>
        ) : null}
      </>
    ),
  },
  {
    id: 'brand',
    label: 'Marca',
    sortKey: 'brand',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (product) => product.brand ?? '—',
  },
  {
    id: 'category',
    label: 'Categoria',
    sortKey: 'category',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (product) => product.categoryName ?? '—',
  },
  {
    id: 'unit',
    label: 'Un.',
    requires: [],
    cell: (product) => {
      const unit = defaultVariant(product)?.unit ?? 'UN'
      return (
        <Tooltip content={UNITS[unit].label}>
          <span>{unit}</span>
        </Tooltip>
      )
    },
  },
  {
    id: 'gtin',
    label: 'Código de barras',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap text-(--text-secondary)',
    cell: (product) => defaultVariant(product)?.gtin ?? '—',
  },
  {
    id: 'ncm',
    label: 'NCM',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap text-(--text-secondary)',
    cell: (product) => defaultVariant(product)?.ncm ?? '—',
  },
  {
    id: 'supplierReference',
    label: 'Fornecedor',
    requires: [],
    className: 'whitespace-nowrap text-(--text-secondary)',
    cell: (product) => defaultVariant(product)?.supplierReference ?? '—',
  },
  {
    // F141: a desta loja — a lista pede para a filial da sessão.
    id: 'location',
    label: 'Localização',
    requires: [],
    className: 'whitespace-nowrap',
    cell: (product) => product.location ?? '—',
  },
  {
    id: 'price',
    label: 'Preço',
    requires: ['pricing.read'],
    sortKey: 'basePrice',
    numeric: true,
    // Um traço, nunca R$ 0,00: produto que ninguém precificou não é produto
    // que a loja dá de graça.
    cell: (product) =>
      product.price?.basePrice == null ? (
        dash
      ) : (
        <Tooltip content={`Tabela ${product.price.priceListName}`}>
          <span>{asMoney(product.price.basePrice)}</span>
        </Tooltip>
      ),
  },
  {
    id: 'cost',
    label: 'Custo médio',
    sortKey: 'averageCost',
    requires: ['catalog.write'],
    numeric: true,
    cell: (product) =>
      product.cost?.averageCost == null
        ? dash
        : asMoney(product.cost.averageCost),
  },
  {
    id: 'margin',
    label: 'Margem',
    sortKey: 'margin',
    requires: ['catalog.write', 'pricing.read'],
    numeric: true,
    // Markup sobre o custo, a mesma conta do cadastro (F123) e do histórico.
    cell: (product) => {
      const cost = product.cost?.averageCost
      const price = product.price?.basePrice
      if (cost == null || price == null) return dash
      const margin = marginFromPrice(
        fromString(cost, UNIT_PRICE_SCALE),
        fromString(price, UNIT_PRICE_SCALE)
      )
      return margin === null ? dash : formatPercent(margin)
    },
  },
  {
    id: 'stock',
    label: 'Estoque',
    requires: ['inventory.read'],
    sortKey: 'onHand',
    numeric: true,
    cell: (product) =>
      !product.tracksStock ? (
        // F028: "não controla" não é zero, e um traço leria como "ninguém
        // contou ainda" — que é o que alguém iria consertar.
        <Badge tone="neutral">não controla</Badge>
      ) : product.stock == null ? (
        dash
      ) : (
        <Tooltip
          content={`Disponível para venda: ${asQuantity(product.stock.available)}`}
        >
          <span
            className={
              outOfStockHere(product)
                ? 'font-(--weight-semibold) text-(--danger)'
                : undefined
            }
          >
            {asQuantity(product.stock.onHand)}
          </span>
        </Tooltip>
      ),
  },
  {
    id: 'available',
    label: 'Disponível',
    sortKey: 'available',
    requires: ['inventory.read'],
    numeric: true,
    cell: (product) =>
      !product.tracksStock || product.stock == null
        ? dash
        : asQuantity(product.stock.available),
  },
  {
    id: 'lastPurchaseAt',
    label: 'Última compra',
    requires: ['catalog.write'],
    className: 'whitespace-nowrap',
    cell: (product) =>
      product.cost?.lastPurchaseAt == null
        ? dash
        : formatDate(product.cost.lastPurchaseAt),
  },
  {
    id: 'priceChangedAt',
    label: 'Preço alterado em',
    requires: ['pricing.read'],
    className: 'whitespace-nowrap',
    cell: (product) =>
      product.priceChangedAt == null
        ? dash
        : formatDate(product.priceChangedAt),
  },
  {
    id: 'condition',
    label: 'Condição',
    sortKey: 'condition',
    requires: [],
    cell: (product) => CONDITION_LABEL[product.condition],
  },
  {
    id: 'warranty',
    label: 'Garantia',
    requires: [],
    className: 'whitespace-nowrap',
    cell: (product) =>
      product.warrantyMonths === null
        ? (product.warrantyNote ?? '—')
        : `${String(product.warrantyMonths)} ${product.warrantyMonths === 1 ? 'mês' : 'meses'}`,
  },
  {
    id: 'weight',
    label: 'Peso',
    requires: [],
    numeric: true,
    cell: (product) =>
      product.weightGrams === null ? dash : `${String(product.weightGrams)} g`,
  },
  {
    id: 'status',
    label: 'Situação',
    sortKey: 'status',
    requires: [],
    cell: (product) =>
      product.status === 'archived' ? (
        <Badge tone="neutral">Arquivado</Badge>
      ) : (
        <Badge tone="success">Ativo</Badge>
      ),
  },
  {
    id: 'createdAt',
    label: 'Cadastrado em',
    requires: [],
    sortKey: 'createdAt',
    className: 'whitespace-nowrap',
    cell: (product) => formatDate(product.createdAt),
  },
  {
    id: 'updatedAt',
    label: 'Alterado em',
    sortKey: 'updatedAt',
    requires: [],
    className: 'whitespace-nowrap',
    cell: (product) => formatDate(product.updatedAt),
  },
]
